/**
 * Payment System Controller
 * Add / Edit System payment systems and currencies
 */

import moment from 'moment'
import { PAYMENT_SYSTEM_TYPES, CURRENCY_CONVERTER_TYPES } from 'app/core/constants'
import { find } from 'app/helpers/object'
import { getName, remove } from 'app/helpers/entity'

// controller mixins
PaymentSystemCtrl.mixes = [
  'onOffCtrlMixin'
]

/**
 * PaymentSystemCtrl controller
 *
 * @class PaymentSystemCtrl
 * @memberof mwApp/settings
 * @see mwApp/settings
 * @mixes mwApp/common.mixin:onOffCtrlMixin
 * @mixes mwApp/common.mixin:tabsCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/settings.controller:PaymentSystemCtrl
 */
/* @ngInject */
export default function PaymentSystemCtrl ($scope, $uibModal, Currency, PaymentSystem, CurrencyConverter, authSvc) {
  var vm = this
  vm.mixes = [['tabsCtrlMixin', '', 'currencies', 'currency_converter']]

  /**
   * Has access
   *
   * @memberOf mwApp/settings.controller:PaymentSystemCtrl
   * @function
   */
  vm.hasAccess = function (access) {
    return authSvc.hasAccess(access)
  }

  /**
   * Loader indicator
   *
   * @type {boolean}
   */
  $scope.loading = true

  /**
   * Payment system types
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @type {Object}
   */
  vm.payment_system_types = PAYMENT_SYSTEM_TYPES

  /**
   * Currency converters types
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @type {Object}
   */
  vm.currency_converter_types = CURRENCY_CONVERTER_TYPES

  vm.attentionMessage = function (currency) {
    return 'If you delete currency:</br></br>' +
      `Payment Sets with selected currency ${currency} will not be available for making Payments.</br></br>` +
      `Making payments with Payment Services with allowed currency ${currency} might not be available if any other currency is not set.</br></br></br>` +
      'Are you sure you want to delete this currency permanently?'
  }

  /**
   * Currencies list
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @name vm.currencies
   * @type {models.Currency[]}
   */

  // load currencies
  Currency.loadAll()
    .to(vm.currencies = [])
    .then(() => { $scope.loading = false })

  /**
   * PaymentSystem services list
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @name vm.payments
   * @type {models.PaymentSystem[]}
   */

  // current default payment
  var defaultPaymentSystem = null
  var cashPaymentSystem = null

  // load payments
  PaymentSystem.loadAll()
    .to(vm.payments = [])
    .then(
      () => {
        vm.payments = vm.payments.filter(p => !['TELR', 'NEWEBPAY'].includes(p.paymentSystemType))
        defaultPaymentSystem = find(p => p.defaultPaymentSystem)(vm.payments) || vm.payments[0]
        cashPaymentSystem = find(p => p.paymentSystemType === 'CASH')(vm.payments)
      }
    )

  // default currency converter
  var defaultConverter = null

  /**
   * Currency converters list
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @name vm.converters
   * @type {models.CurrencyConverter[]}
   */

  // load currency converters
  CurrencyConverter.loadAll()
    .to(vm.converters = [])
    .then(
      () => {
        defaultConverter = find(c => c.defaultConverter)(vm.converters) || vm.converters[0]
      }
    )

  /**
   * On | Off payment
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @param  {models.PaymentSystem} payment
   */
  vm.onOffPayment = function (payment) {
    vm.onOff.apply(this, arguments)
      .then(
        () => {
          if (!payment.enabled && payment === defaultPaymentSystem) {
            defaultPaymentSystem = cashPaymentSystem
            defaultPaymentSystem.defaultPaymentSystem = true
          }
        }
      )
  }

  /**
   * Delete currency
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @param  {models.Currency} currency
   */
  vm.deleteCurrency = function (currency) {
    currency._delete()
      .then(remove(vm.currencies)(currency))
  }

  /**
   * Edit currency
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @function
   * @param  {models.Currency} currency
   */
  vm.editCurrency = null

  /**
   * Create currency
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @function
   * @param  {models.Currency} currency
   */
  vm.addCurrency = vm.editCurrency = function (currency) {
    var isNew = !currency
    var initialCurrency = angular.copy(currency) // initial state
    $uibModal.open({
      templateUrl: 'components/settings/views/addCurrency.html',
      controller: 'AddCurrencyCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        currency: function () {
          return !isNew
            ? currency
            : new Currency()
        },
        existingCurrencies: function () {
          return vm.currencies.map(getName)
        }
      }
    }).result
      .then(
        function close (currency) {
          if (isNew) {
            vm.currencies.push(currency)
          }
        },
        function dismiss () {
          if (!isNew) {
            angular.copy(initialCurrency, currency) // restore currency
          }
        }
      )
  }

  /**
   * Set payment as default
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @param  {models.PaymentSystem} payment
   */
  vm.setDefaultPayment = function (payment) {
    payment._default()
      .then(
        () => {
          if (defaultPaymentSystem && payment !== defaultPaymentSystem) {
            defaultPaymentSystem.defaultPaymentSystem = false
          }
          defaultPaymentSystem = payment
        }
      )
  }

  /**
   * Get payment controller & template url by type
   *
   * @param type
   * @returns {{template: string, controller: string}}
   */
  const getPaymentAttributes = (type) => ({
    CASH: {
      template: 'components/settings/views/cashSettings.html',
      controller: 'AddCashPaymentServiceCtrl as vm'
    },
    EXTERNAL_PAYMENTS: {
      template: 'components/settings/views/externalPaymentSettings.html',
      controller: 'AddExternalPaymentServiceCtrl as vm'
    },
    AUTHORIZE_NET: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    PAYPAL: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TELR: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    NEWEBPAY: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    GR4VY_GATEWAY: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    CCPP: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TRUE_MONEY: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TRUE_ONLINE: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TRUE_MOVE_H: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    AIRWALLEX: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    GOCARDLESS: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    }
  }[type])

  /**
   * Edit payment
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @param  {models.PaymentSystem} payment
   */
  vm.editPayment = function (payment) {
    const { template, controller } = getPaymentAttributes(payment.paymentSystemType)
    $uibModal.open({
      templateUrl: template,
      controller: controller,
      animation: false,
      size: 'lg',
      resolve: {
        payment: () => PaymentSystem.load(payment).then(data => Object.assign(data, { $fromLocation: 'GlobalPayment' }))
      }
    }).result
      .then(
        (p) => {
          // only field that could be changed in form, that is shown in the list -> update it
          payment.live = p.live
        }
      )
  }

  /**
   * Format currency converter rate
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @function
   * @param  {Number} seconds refresh rate
   */
  vm.formatRefreshRate = (seconds) => {
    if (seconds) {
      const duration = moment.duration(seconds * 60, 'seconds') // multiply by 60, because in the `refreshRateSec` field already minutes
      const [h, m, s] = [duration.hours(), duration.minutes(), duration.seconds()]
      return (h ? `${h} hours ` : '') + (m ? `${m} mins ` : '') + (s ? `${s} seconds` : '')
    }
    return 'Live'
  }

  /**
   * Edit currency converter
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @param  {models.CurrencyConverter} converter
   */
  vm.editConverter = function (converter) {
    $uibModal.open({
      templateUrl: 'components/settings/views/addCurrencyConverter.html',
      controller: 'AddCurrencyConverterCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        converter: () => Object.assign(converter, { $fromLocation: 'GlobalPayment' })
      }
    })
  }

  /**
   * Set converter as default
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @param  {models.CurrencyConverter} converter
   */
  vm.setDefaultConverter = function (converter) {
    converter._default()
      .then(
        () => {
          if (defaultConverter && converter !== defaultConverter) {
            defaultConverter.defaultConverter = false
          }
          defaultConverter = converter
        }
      )
  }
}
